import React, { useState, useEffect, useRef, useMemo } from 'react';

import { translatedMessage } from '../../service/LanguageService';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ProgressSpinner } from 'primereact/progressspinner';
import DeleteModalComponent from '../DeleteModalComponent';
import DataTableUtils from '../../utilities/DataTableUtils';
import SecurityService from '../../service/SecurityService';
import { CompetitionService } from '../../service/CompetitionService';
import CompetitionUtils from '../../utilities/CompetitionUtils';
import CompetitionBibliographyEditDialog from './CompetitionBibliographyEditDialog';

const CompetitionBibliographyListComponent = (props) => {
    const [isLoading, setIsLoading] = useState(true);
    const [competition, setCompetition] = useState(null);
    const [hasEditPermission, setHasEditPermission] = useState(false);
    const [bibliographyList, setBibliographyList] = useState(null);
    const [selectedValue, setSelectedValue] = useState(null);
    const [showAddDialog, setShowAddDialog] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);

    const toast = useRef(null);
    const dt = useRef(null);

    const competitionService = useMemo(() => new CompetitionService(), []);

    useEffect(() => {
        setIsLoading(true)
        setCompetition(props?.competition);

        const checkEditPermission = async () => {
            setHasEditPermission(await SecurityService.check('CAN_EDIT_JOB_COMPETITION', props?.competition?.id));
        };

        checkEditPermission();
        getBibliography()

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props]);

    const getBibliography = async () => {
        await competitionService.getCompetitionBibliographyList(props?.competition?.id)
            .then((_itList) => {
                // console.log("itList: ", _itList);
                setBibliographyList(_itList);
                setIsLoading(false);
            })
            .catch((error) => error && toast?.current?.show({ severity: 'error', summary: translatedMessage(error), life: 5000 }));
    };

    const afterSave = (savedCompetitionBibliography, success, error) => {
        if (success) {
            getBibliography();
            toast?.current?.show({ severity: 'success', summary: translatedMessage('generic.save.success') });
        } else {
            toast?.current?.show({ severity: 'error', summary: translatedMessage(error), life: 5000 });
        }
    };

    // Table
    const actionBodyTemplate = (rowData) => {
        return (
            <div className="actions flex flex-wrap align-items-center justify-content-end">
                {hasEditPermission && <>
                    <Button icon="pi pi-pencil" className="p-button-rounded p-button-info m-1" onClick={() => handleEdit(rowData)}
                        tooltip={translatedMessage("generic.edit")} tooltipOptions={{ position: 'top' }}
                        disabled={!CompetitionUtils.canEdit(competition)} />
                    <Button icon="pi pi-trash" className="p-button-rounded p-button-danger m-1" onClick={() => handleDelete(rowData)}
                        tooltip={translatedMessage("generic.delete")} tooltipOptions={{ position: 'top' }}
                        disabled={!CompetitionUtils.canEdit(competition)} />
                </>}
            </div>
        );
    };

    // Actions
    const handleNew = () => {
        setSelectedValue(null)
        setShowAddDialog(true)
    }

    const handleEdit = (data) => {
        // console.log("handleEdit: ", data);
        setSelectedValue(data)
        setShowAddDialog(true)
    }

    const handleDelete = (data) => {
        setSelectedValue(data)
        setShowDeleteDialog(true)
    }

    const deleteRecord = () => {
        competitionService.deleteCompetitionBibliography(selectedValue.id)
            .then(() => {
                getBibliography()
                setShowDeleteDialog(false);

                toast.current.show({ severity: 'success', summary: translatedMessage("generic.delete.success") });
            })
            .catch(error => {
                toast.current.show({ severity: 'error', summary: translatedMessage(error), life: 5000 });
            })
    };

    if (isLoading) {
        return (
            <div className='w-full flex align-items-center'>
                <Toast ref={toast}></Toast>
                <ProgressSpinner />
            </div>
        );
    } else {
        return (
            <>
                <Toast ref={toast} />
                <div className='col-12 flex flex-row align-items-center mt-5'>
                    <h5 className='mb-0'>{translatedMessage('competition.requirements.bibliography.title')}</h5>
                    <div>
                        <Button
                            label={translatedMessage('generic.add')}
                            icon="pi pi-plus"
                            className="pcn-button-slim-with-icon p-button-primary ml-3"
                            onClick={handleNew}
                            visible={hasEditPermission}
                            disabled={!CompetitionUtils.canEdit(competition)}
                        />
                    </div>
                </div>
                <div className='col-12'>
                    <DataTable
                        ref={dt}
                        value={bibliographyList}
                        dataKey="id"
                        // paginator
                        rows={DataTableUtils.defalRowsPerPage()}
                        rowsPerPageOptions={DataTableUtils.rowsPerPageOptions()}
                        className="datatable-responsive"
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                        currentPageReportTemplate={"{first} - {last} " + translatedMessage('generic.of') + " {totalRecords}"}
                        emptyMessage={translatedMessage("generic.tableEmptyMessage")}
                        responsiveLayout="scroll"
                    >
                        <Column
                            field="bibliography"
                            header={translatedMessage("competition.requirements.bibliography")}
                            headerStyle={{ width: '25%', minWidth: '8rem' }}
                        />
                        <Column
                            field="topic"
                            header={translatedMessage("competition.requirements.topic")}
                            headerStyle={{ width: '65%', minWidth: '8rem' }}
                        />
                        <Column body={actionBodyTemplate}></Column>
                    </DataTable>
                </div>

                {showAddDialog &&
                    <CompetitionBibliographyEditDialog
                        competition={competition}
                        bibliography={selectedValue}
                        visible={showAddDialog}
                        afterSave={afterSave}
                        onClose={() => setShowAddDialog(false)}
                    />
                }

                {showDeleteDialog &&
                    <DeleteModalComponent
                        visible={showDeleteDialog}
                        item={selectedValue && selectedValue.institutionName}
                        closeDialog={() => setShowDeleteDialog(false)}
                        deleteRecord={() => deleteRecord()}
                    />
                }
            </>
        );
    }
};

export default CompetitionBibliographyListComponent;
